import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listProjectMeerwerken,
  createMeerwerk,
  deleteMeerwerk,
  getMeerwerkDefaults,
  MEERWERK_TYPE_LABELS_EN,
  MEERWERK_UNIT_LABELS_EN,
  MEERWERK_STATUS_LABELS_EN,
  type Meerwerk,
  type MeerwerkType,
  type MeerwerkUnit,
  type CreateMeerwerkPayload,
} from '@/api/meerwerken';
import { useAuthStore } from '@/stores/auth';
import { useMeerwerkFormStore } from '@/stores/meerwerkForm';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';

interface Props {
  projectId: number;
  taskId: number | null;
  taskDate: string | null;
}

const TYPE_KEYS = Object.keys(MEERWERK_TYPE_LABELS_EN) as MeerwerkType[];
const UNIT_KEYS = Object.keys(MEERWERK_UNIT_LABELS_EN) as MeerwerkUnit[];

export function MeerwerkenSection({ projectId, taskId, taskDate }: Props) {
  const odooId = useAuthStore((s) => s.user?.odooId ?? null);
  const [items, setItems] = useState<Meerwerk[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<MeerwerkType>('wachturen');
  const sectionRef = useRef<HTMLDivElement>(null);

  // "Uren melden" elsewhere on the page opens THIS form, preset to its type.
  // A request left in the store from an earlier page must not pop the form open on mount.
  const openRequest = useMeerwerkFormStore((s) => s.request);
  const seenNonce = useRef(openRequest?.nonce ?? 0);
  useEffect(() => {
    if (!openRequest || openRequest.nonce === seenNonce.current) return;
    seenNonce.current = openRequest.nonce;
    setFormType(openRequest.type);
    setShowForm(true);
    sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [openRequest]);

  const load = useCallback(async () => {
    if (!odooId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await listProjectMeerwerken(projectId, {
        subcontractorId: odooId,
        ...(taskId ? { taskId } : {}),
      });
      setItems(data);
    } catch {
      // best-effort
    } finally {
      setLoading(false);
    }
  }, [projectId, taskId, odooId]);

  useEffect(() => {
    load();
  }, [load]);

  const myItems = items; // backend already scopes to project; status displayed alongside

  return (
    <div ref={sectionRef} className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide">Meerwerken / Notes</h2>
        {!showForm && (
          <button
            type="button"
            onClick={() => { setFormType('wachturen'); setShowForm(true); }}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-td-blue/10 text-td-blue hover:bg-td-blue/20 transition-colors"
            aria-label="Meerwerk toevoegen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {loading && (
        <div className="py-4 text-center">
          <div className="inline-block w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Loading…</span>
          </div>
        </div>
      )}

      {!loading && myItems.length === 0 && !showForm && (
        <p className="text-sm text-td-muted py-2">Nog geen meerwerken of notities</p>
      )}

      {!loading && myItems.length > 0 && (
        <ul className="space-y-2">
          {myItems.map((m) => (
            <MeerwerkRow key={m.id} item={m} onChanged={load} />
          ))}
        </ul>
      )}

      {showForm && (
        <MeerwerkCreateForm
          key={openRequest?.nonce ?? 0}
          initialType={formType}
          projectId={projectId}
          taskId={taskId}
          taskDate={taskDate}
          onCancel={() => setShowForm(false)}
          onCreated={async () => {
            setShowForm(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function MeerwerkRow({ item, onChanged }: { item: Meerwerk; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const canDelete = item.status === 'pending';

  const onDelete = async () => {
    if (!canDelete) return;
    if (!confirm('Delete this entry?')) return;
    setBusy(true);
    try {
      await deleteMeerwerk(item.id);
      await onChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const statusColors: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800',
    on_po: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-600',
  };

  const fmtQty = () => {
    if (!item.quantity && !item.unit_price) return '';
    if (item.unit === 'hours') return `${item.quantity} h`;
    if (item.unit === 'pieces') return `${item.quantity} pcs`;
    if (item.unit === 'km') return `${item.quantity} km`;
    if (item.unit === 'lump_sum' && item.unit_price > 0) return `€ ${item.unit_price.toFixed(2)}`;
    if (item.quantity && item.unit_price > 0) return `${item.quantity} × €${item.unit_price.toFixed(2)}`;
    if (item.quantity) return String(item.quantity);
    if (item.unit_price > 0) return `€ ${item.unit_price.toFixed(2)}`;
    return '';
  };

  return (
    <li className="rounded-lg border border-gray-100 p-3">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-td-blue/10 text-td-blue shrink-0">
          {MEERWERK_TYPE_LABELS_EN[item.type] ?? item.type}
        </span>
        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${statusColors[item.status] ?? 'bg-gray-100 text-gray-600'}`}>
          {MEERWERK_STATUS_LABELS_EN[item.status] ?? item.status}
        </span>
        <span className="text-sm font-bold text-td-navy">{item.name}</span>
        <span className="text-sm text-td-navy ml-auto whitespace-nowrap">{fmtQty()}</span>
      </div>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-td-muted">
        <span>{item.date}</span>
        {item.task_name && <span>· {item.task_name}{item.task_is_interventie && <InterventieBadge className="ml-1.5 align-middle" />}</span>}
        {item.unit_price > 0 && item.unit && item.unit !== 'lump_sum' && (
          <span>· €{item.unit_price.toFixed(2)} / {MEERWERK_UNIT_LABELS_EN[item.unit].toLowerCase()}</span>
        )}
        {item.purchase_order_name && <span>· PO {item.purchase_order_name}</span>}
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="ml-auto text-red-600 hover:text-red-700 font-semibold disabled:opacity-50"
          >
            Delete
          </button>
        )}
      </div>
      {item.note && <p className="text-xs text-td-muted mt-1.5 pl-0.5">{item.note}</p>}
    </li>
  );
}

function MeerwerkCreateForm({
  initialType = 'wachturen',
  projectId,
  taskId,
  taskDate,
  onCancel,
  onCreated,
}: {
  initialType?: MeerwerkType;
  projectId: number;
  taskId: number | null;
  taskDate: string | null;
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const [type, setType] = useState<MeerwerkType>(initialType);
  // Werkuren defaults to the hours unit (it's billed per hour).
  const [unit, setUnit] = useState<MeerwerkUnit | ''>(initialType === 'wachturen' ? 'hours' : '');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isKm = type === 'kilometervergoeding';
  // Binddraad is counted in rolls, at a price per roll Thermoduct prefills.
  const isBinddraad = type === 'binddraad';
  // km and binddraad need no description — the type + count say it all.
  const effectiveName = name.trim() || (isKm ? 'Kilometervergoeding' : isBinddraad ? 'Binddraad' : '');

  // The type the latest change picked, so a slow defaults fetch cannot prefill the
  // binddraad price into a type the user has already moved on from.
  const typeRef = useRef(type);

  const handleTypeChange = (newType: MeerwerkType) => {
    typeRef.current = newType;
    setType(newType);
    if (newType === 'kilometervergoeding') {
      setUnit('km');
      setUnitPrice('');
    } else if (newType === 'wachturen') {
      setUnit('hours'); // werkuren → per hour
    } else if (newType === 'binddraad') {
      setUnit('pieces');
      getMeerwerkDefaults()
        .then((d) => {
          if (typeRef.current === 'binddraad' && d.binddraad_unit_price !== null) {
            setUnitPrice(String(d.binddraad_unit_price));
          }
        })
        .catch(() => {}); // no default → the field stays empty and the server fills it
    } else if (unit === 'km') {
      setUnit('');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveName) return;
    // km is server-priced per kilometer — a count is mandatory (matches the API guard).
    if (isKm && !(Number(quantity) > 0)) {
      setError('Vul het aantal kilometers in.');
      return;
    }
    if (isBinddraad && !(Number(quantity) > 0)) {
      setError('Enter the number of rolls.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: CreateMeerwerkPayload = {
        name: effectiveName,
        type,
        ...(taskDate ? { date: taskDate } : {}),
      };
      if (unit !== '') payload.unit = unit;
      const q = Number(quantity);
      if (quantity.trim() !== '' && Number.isFinite(q) && q >= 0) payload.quantity = q;
      if (!isKm) {
        const up = Number(unitPrice);
        if (Number.isFinite(up) && up >= 0 && unitPrice.trim() !== '') payload.unit_price = up;
      }
      if (note.trim()) payload.note = note.trim();
      if (taskId) payload.task_id = taskId;
      await createMeerwerk(projectId, payload);
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="mt-3 space-y-3 border-t border-gray-100 pt-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="mw-type" className="block text-xs font-bold text-td-muted uppercase tracking-wide mb-1">
            Type
          </label>
          <select
            id="mw-type"
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as MeerwerkType)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-td-navy bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/40"
          >
            {TYPE_KEYS.map((t) => (
              <option key={t} value={t}>
                {MEERWERK_TYPE_LABELS_EN[t]}
              </option>
            ))}
          </select>
        </div>
        {!isKm && !isBinddraad && (
          <div>
            <label htmlFor="mw-unit" className="block text-xs font-bold text-td-muted uppercase tracking-wide mb-1">
              Unit <span className="text-td-muted font-normal normal-case">(optional)</span>
            </label>
            <select
              id="mw-unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value as MeerwerkUnit | '')}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-td-navy bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/40"
            >
              <option value="">— none —</option>
              {UNIT_KEYS.filter((u) => u !== 'km').map((u) => (
                <option key={u} value={u}>
                  {MEERWERK_UNIT_LABELS_EN[u]}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <label htmlFor="mw-name" className="block text-xs font-bold text-td-muted uppercase tracking-wide mb-1">
          Description {(isKm || isBinddraad) && <span className="text-td-muted font-normal normal-case">(optional)</span>}
        </label>
        <input
          id="mw-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required={!isKm && !isBinddraad}
          maxLength={500}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-td-navy bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/40"
          placeholder={isKm ? 'bv. werf → werf (optioneel)' : 'e.g. waiting for supplier'}
        />
      </div>

      <div className={`grid gap-3 ${isKm ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <div>
          <label htmlFor="mw-qty" className="block text-xs font-bold text-td-muted uppercase tracking-wide mb-1">
            {isKm ? 'Kilometers' : isBinddraad ? 'Rolls' : <>Quantity <span className="text-td-muted font-normal normal-case">(optional)</span></>}
          </label>
          <input
            id="mw-qty"
            type="number"
            step="any"
            min="0"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-td-navy bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/40"
            placeholder="0"
          />
        </div>
        {!isKm && (
          <div>
            <label htmlFor="mw-price" className="block text-xs font-bold text-td-muted uppercase tracking-wide mb-1">
              {isBinddraad ? 'Price per roll' : <>Price per unit <span className="text-td-muted font-normal normal-case">(optional)</span></>}
            </label>
            <input
              id="mw-price"
              type="number"
              step="any"
              min="0"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-td-navy bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/40"
              placeholder="0.00"
            />
          </div>
        )}
      </div>

      <div>
        <label htmlFor="mw-note" className="block text-xs font-bold text-td-muted uppercase tracking-wide mb-1">
          Note <span className="text-td-muted font-normal normal-case">(optional)</span>
        </label>
        <textarea
          id="mw-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={5000}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-td-navy bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/40 resize-none"
          placeholder="Extra context"
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving || !effectiveName}
          className="px-4 py-2 text-sm font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-semibold text-td-muted hover:text-td-navy transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
